import React, { useEffect, useState } from 'react'

import { useWebWoodContext } from 'context/WebWoodContext'
import store, { dispatch } from 'store'
import editorSlice from 'store/features/editor/slice'
import * as Styled from './styled'
import { ICanvasTextEditorProps, ISelectionSpan } from './types'
import { useEventsCallbacks } from './hooks/useEventsCallbacks'
import { useEnsureFontLoaded } from './hooks/useEnsureFontLoaded'
import { useCursor } from './hooks/useCursor'
import { placeCursorAtEnd, useInnerTextSync } from './hooks/useInnerTextSync'

const CanvasTextEditor: React.FC<ICanvasTextEditorProps> = ({
  itemId,
  assetId,
  textData,
  width,
  height,
  canvasScale,
  rotation,
  flipX
}) => {
  const engineId = `${itemId}-${assetId}`
  const [selectionSpans, setSelectionSpans] = useState<ISelectionSpan[]>([])

  // Snapshot the entry point at mount via the store directly. Using
  // `useSelector` would resubscribe and rerender on the self-clear below;
  // we only ever need the value once.
  const [entryPoint] = useState(
    () => store.getState().editor.editingTextEntryPoint
  )

  const { engine, updateObjectById } = useWebWoodContext()
  useEnsureFontLoaded(textData)
  const { editorRef, initialTextRef } = useInnerTextSync(
    textData.text,
    !!entryPoint
  )
  const { cursorRef } = useCursor(
    setSelectionSpans,
    editorRef,
    engineId,
    itemId,
    engine
  )
  const {
    handleInput,
    handleKeyDown,
    handlePaste,
    handleEditorPointerDown,
    handleDoubleClick,
    handleCompositionStart,
    handleCompositionEnd,
    stopPointer,
    commit,
    hitTest,
    setSelectionAtIndex
  } = useEventsCallbacks(
    canvasScale,
    updateObjectById,
    engineId,
    editorRef,
    initialTextRef,
    itemId,
    assetId,
    engine,
    rotation,
    flipX
  )

  // Mount-only: if edit mode was entered with a click position, focus the
  // editor and place the caret at the hit-tested glyph. Falls back to
  // end-of-text if the click landed outside any glyph (defensive — the
  // engine's hit-test snaps to the nearest cursor slot, so this is rare).
  useEffect(() => {
    if (!entryPoint) return
    const el = editorRef.current
    if (!el) return
    el.focus()
    const idx = hitTest(entryPoint.clientX, entryPoint.clientY)
    if (idx === null) {
      placeCursorAtEnd(el)
    } else {
      setSelectionAtIndex(idx)
      // Mirror the iOS WebKit double-apply in handleEditorPointerDown:
      // synthesized post-touch mouse events can race and snap the caret
      // to end-of-text. Re-apply on the next frame so our index sticks.
      requestAnimationFrame(() => setSelectionAtIndex(idx))
    }
    dispatch(editorSlice.actions.setEditingTextEntryPoint(null))
    // Mount-only: the entry point is snapshotted at mount; callbacks read
    // from refs / closures over stable values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Place the DOM Selection at the source-text index `target`. Walks the
   * contentEditable to find the right text node and offset, treating each
   * `<br>` as a single `\n` character (mirrors `indexFromDomPosition`).
   */
  return (
    <>
      <Styled.EditorSurface
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onBlur={commit}
        $width={width}
        $height={height}
        $fontPostScriptName={textData.fontPostScriptName}
        $fontSize={textData.fontSize}
        $lineHeight={textData.lineHeight}
        $textAlign={textData.textAlign}
      />
      <Styled.PointerCatcher
        $width={width}
        $height={height}
        onPointerDown={handleEditorPointerDown}
        onMouseDown={stopPointer}
        onClick={stopPointer}
        onDoubleClick={handleDoubleClick}
        onContextMenu={stopPointer}
      />
      {selectionSpans.map(s => (
        <Styled.SelectionRect
          key={`${s.y}-${s.x}-${s.width}`}
          $x={s.x}
          $y={s.y}
          $width={s.width}
          $height={s.height}
        />
      ))}
      <Styled.Cursor ref={cursorRef} $color={textData.fillColor} />
    </>
  )
}

export default CanvasTextEditor
